import axios from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでファイルのアップロードが成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

function AxiosPost(url: string, formData: FormData, token?: string) {
  if (token) {
    return axios.post(url, formData, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.post(url, formData);
  }
}

export interface IUploadConfFileParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * ファイルオブジェクト
   * 基本的に1つずつアップロードされるけれど、アップロード個数の上限の変更によって
   * 複数個を配列で持てるように設定
   */
  files: File[];
  /**
   * 設定ファイルの種類
   */
  type: string;
}

/**
 * 設定ファイルをアップロードします
 * 基本的に1つずつアップロードされるけれど、アップロード個数の上限の変更によって
 * 複数のファイルが入ってきた場合にも対応可
 * @function uploadConfFile
 * @param {IUploadConfFileParams} params - 設定ファイルアップロードパラメータ
 */
export function uploadConfFile(params: IUploadConfFileParams): void {
  const { authService, projectid, files, type } = params;
  const url = WebAPI.UPLOAD_CONFFILE(projectid, type);
  if (authService) {
    const promises = files.map((file) => {
      const formData = new FormData();
      formData.append("file", file);
      return new Promise((resolve, reject) => {
        authService.getToken({
          success: (token) => {
            AxiosPost(url, formData, token)
              .then((res) => {
                resolve(res);
              })
              .catch((err) => {
                reject(err);
              });
          },
          fail: (err) => {
            reject(err);
          },
        });
      });
    });
    Promise.all(promises)
      .then(() => {
        params.success(200);
      })
      .catch((err) => {
        const { id, status } = getErrorStatus(err);
        if (status === 401) {
          uploadConfFileRetry401(authService, url, files, params);
        } else {
          params.fail(id, status);
        }
      });
  } else {
    const promises = files.map((file) => {
      const formData = new FormData();
      formData.append("file", file);
      return new Promise((resolve, reject) => {
        AxiosPost(url, formData)
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            reject(err);
          });
      });
    });
    Promise.all(promises)
      .then(() => {
        params.success(200);
      })
      .catch((err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      });
  }
}

/**
 * 設定ファイルをアップロードします
 * @function uploadConfFileRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {File[]} files - ファイルオブジェクトの配列
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function uploadConfFileRetry401(
  authService: AuthService,
  url: string,
  files: File[],
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      const promises = files.map((file) => {
        const formData = new FormData();
        formData.append("file", file);
        return new Promise((resolve, reject) => {
          AxiosPost(url, formData, token)
            .then((res) => {
              resolve(res);
            })
            .catch((err) => {
              reject(err);
            });
        });
      });
      Promise.all(promises)
        .then(() => {
          params.success(200);
        })
        .catch((err) => {
          const { id, status } = getErrorStatus(err);
          params.fail(id, status);
        });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
